<template>
  <el-card class="panel-card" shadow="never">
    <template #header>
      <div class="card-header">
        <span>Key 列表</span>
        <el-tag effect="plain">{{ keys.length }} 个</el-tag>
      </div>
    </template>
    <el-table :data="keys" v-loading="loading" empty-text="暂无 API Key" stripe>
      <el-table-column label="API Key" min-width="300">
        <template #default="{ row }">
          <div class="key-cell">
            <span class="mono">{{ row.key }}</span>
            <el-button size="small" plain @click="copyKey(row.key)">
              {{ copiedKey === row.key ? '已复制' : '复制' }}
            </el-button>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="额度" width="150">
        <template #default="{ row }">
          <el-input-number v-if="editingId === row.id" v-model="draft.quota" :min="0" size="small" controls-position="right" />
          <span v-else>{{ row.quota }}</span>
        </template>
      </el-table-column>
      <el-table-column label="已用" width="150">
        <template #default="{ row }">
          <el-input-number v-if="editingId === row.id" v-model="draft.used" :min="0" size="small" controls-position="right" />
          <span v-else>{{ row.used }}</span>
        </template>
      </el-table-column>
      <el-table-column label="剩余" width="110">
        <template #default="{ row }">{{ remainingQuota(row) }}</template>
      </el-table-column>
      <el-table-column label="过期时间" width="210">
        <template #default="{ row }">
          <el-date-picker
            v-if="editingId === row.id"
            v-model="draft.expires_at"
            type="datetime"
            format="YYYY-MM-DD HH:mm:ss"
            value-format="YYYY-MM-DD HH:mm:ss"
            size="small"
            placeholder="YYYY-MM-DD HH:mm:ss"
          />
          <span v-else>{{ formatTime(expireValue(row)) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="120">
        <template #default="{ row }">
          <el-select v-if="editingId === row.id" v-model="draft.status" size="small">
            <el-option label="启用" :value="1" />
            <el-option label="禁用" :value="0" />
          </el-select>
          <el-tag v-else :type="row.status === 1 ? 'success' : 'info'" effect="plain">
            {{ row.status === 1 ? '启用' : '禁用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="170" fixed="right">
        <template #default="{ row }">
          <template v-if="editingId === row.id">
            <el-button size="small" type="primary" @click="save(row)">保存</el-button>
            <el-button size="small" @click="cancel">取消</el-button>
          </template>
          <template v-else>
            <el-button size="small" type="primary" plain @click="edit(row)">编辑</el-button>
            <el-button size="small" type="danger" plain @click="$emit('delete', row)">删除</el-button>
          </template>
        </template>
      </el-table-column>
    </el-table>
  </el-card>
</template>

<script>
export default {
  name: 'KeyTable',
  props: {
    keys: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update', 'delete'],
  data() {
    return {
      editingId: null,
      copiedKey: null,
      copiedTimer: null,
      draft: {
        quota: 0,
        used: 0,
        status: 1,
        expires_at: ''
      }
    }
  },
  methods: {
    remainingQuota(item) {
      const quota = this.editingId === item.id ? this.draft.quota : item.quota
      const used = this.editingId === item.id ? this.draft.used : item.used
      return Math.max(0, Number(quota || 0) - Number(used || 0))
    },
    edit(item) {
      this.editingId = item.id
      this.draft = {
        quota: Number(item.quota || 0),
        used: Number(item.used || 0),
        status: Number(item.status),
        expires_at: this.formatTime(this.expireValue(item))
      }
    },
    cancel() {
      this.editingId = null
    },
    async copyKey(key) {
      if (!key) return

      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(key)
      } else {
        const textarea = document.createElement('textarea')
        textarea.value = key
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
      }

      this.copiedKey = key
      window.clearTimeout(this.copiedTimer)
      this.copiedTimer = window.setTimeout(() => {
        this.copiedKey = null
      }, 1500)
    },
    save(item) {
      const expiresAt = this.formatTime(this.draft.expires_at)
      console.log('[KeyTable] save api key draft', {
        id: item.id,
        raw_expires_at: this.draft.expires_at,
        expires_at: expiresAt,
        draft: this.draft
      })
      this.$emit('update', {
        ...item,
        ...this.draft,
        expires_at: expiresAt,
        expires_at_text: expiresAt
      })
      this.editingId = null
    },
    formatTime(value) {
      if (!value) {
        return ''
      }
      if (typeof value === 'string') {
        const trimmed = value.trim()
        if (!trimmed) {
          return ''
        }
        const match = trimmed.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})/)
        if (match) {
          return `${match[1]} ${match[2]}`
        }
      }
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return value
      }
      const pad = (item) => String(item).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    },
    expireValue(item) {
      return item?.expires_at_text || item?.expires_at || item?.ExpiresAt || item?.expiresAt || ''
    }
  },
  beforeUnmount() {
    window.clearTimeout(this.copiedTimer)
  }
}
</script>
